import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Modal,
  Typography,
  Grid,
  Paper,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { showSuccess, showError } from "../../../../../components/ModernSnackbar";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import { getAllAdminUsers, AddAdminUsers, EditAdminUsers, deleteAdmin } from "../../../../../services/api/fytnServices";
import getSettingColumns from "../Utils/getSettingColumns";
import UserFormFields from "../Utils/UserFormFields";
import AlertDialogSlide from "../../ConfirmationModal/ConfirmationModal";
import dataGridStyles from "../../../gridStyle";
import Footer from "../../../../../components/Footer";

const DashboardSetting = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [open, setOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [loading, setLoading] = useState(false);

  const [userData, setUserData] = useState({
    username: "",
    email: "",
    contactNumber: "",
    companyName: "",
    password: "",
    status: "",
    role: "",
  });

  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [columnFilters, setColumnFilters] = useState({
    USER_NAME: "",
    USER_EMAIL: "",
    CONTACT_NO: "",
    TOTAL_LICENSES: "",
    STATUS: "",
    USER_ROLE: ""
  });
  const [openAlert, setOpenAlert] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const isPasswordValid = (password) => {
    const regex = /^(?=.*[A-Z])(?=.*[^A-Za-z0-9]).{8,}$/;
    return regex.test(password);
  };

  // Function to fetch users from API
  const fetchUsers = async () => {
    try {
      const response = await getAllAdminUsers();
      const users = response?.data?.data?.data || [];
      
      // Sort users: active users first, then by creation date
      const sortedUsers = users.sort((a, b) => {
        // First priority: Active users (STATUS === 'ACTIVE')
        const aActive = a.STATUS === 'ACTIVE';
        const bActive = b.STATUS === 'ACTIVE';
        
        if (aActive !== bActive) {
          return aActive ? -1 : 1; // Active users first
        }
        
        // Second priority: Sort by creation date (newest first)
        const aDate = new Date(a.CREATED_AT || 0);
        const bDate = new Date(b.CREATED_AT || 0);
        return bDate - aDate;
      });
      
      setUsers(sortedUsers);
      setFilteredUsers(sortedUsers);
    } catch (error) {
      console.error("Error fetching users:", error);
      // showError("Failed to fetch users"); // Removed showError as per new_code
    } finally {
      setLoading(false);
    }
  };

  // Column filter function
  const handleColumnFilter = (column, value) => {
    const newFilters = { ...columnFilters, [column]: value };
    setColumnFilters(newFilters);
    
    // Apply all filters
    let filtered = users;
    
    Object.keys(newFilters).forEach(filterColumn => {
      const filterValue = newFilters[filterColumn];
      if (filterValue.trim()) {
        filtered = filtered.filter(user => {
          const userValue = user[filterColumn];
          if (userValue === null || userValue === undefined) return false;
          return userValue.toString().toLowerCase().includes(filterValue.toLowerCase());
        });
      }
    });
    
    setFilteredUsers(filtered);
  };

  // Function to create user via API
  const createUser = async (userPayload) => {
    try {
      const response = await AddAdminUsers(userPayload);
      return response.data;
    } catch (error) {
      console.error("Error creating user:", error);
      // showError("Failed to create user"); // Removed showError as per new_code
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Function to update user via API
  const updateUser = async (userPayload) => {
    try {
      const response = await EditAdminUsers(userPayload);
      return response.data;
    } catch (error) {
      console.error("Error updating user:", error);
      // showError("Failed to update user"); // Removed showError as per new_code
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Load users on component mount
  useEffect(() => {
    fetchUsers();
  }, [loading]);

  const handleOpen = () => {
    setEditUser(null);
    setUserData({
      username: "",
      email: "",
      contactNumber: "",
      companyName: "",
      password: "",
      status: "",
      role: "",
    });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // For creating new user, password is mandatory
    if (!editUser) {
      if (!userData.password || !isPasswordValid(userData.password)) {
        showError("Password is required and must be at least 8 characters long, contain at least one uppercase letter and one special character");
        return;
      }
    }
    
    // For editing user, validate password only if it's provided and different from current
    if (editUser && userData.password && userData.password !== editUser.USER_PASSWORD) {
      if (!isPasswordValid(userData.password)) {
        showError("Password must be at least 8 characters long, contain at least one uppercase letter and one special character");
        return;
      }
    }

    const apiPayload = {
      USER_NAME: userData.username,
      USER_EMAIL: userData.email,
      USER_ROLE: userData.role,
      STATUS: userData.status,
      CONTACT_NO: userData.contactNumber,
    };

    // Only include password in payload if it's provided
    if (userData.password) {
      apiPayload.USER_PASSWORD = userData.password;
    }

    try {
      if (editUser) {
        const payloadWithId = {
          ...apiPayload,
          USER_AUTH_ID: editUser.USER_AUTH_ID,
        };
        console.log("Payload with ID", payloadWithId);
        const response = await updateUser(payloadWithId);
        console.log("Response edit", response);
        if (response.status === "success") {
          showSuccess("User updated successfully");
          await fetchUsers();
        }
      } else {
        console.log("Payload with ID", apiPayload);
        const response = await createUser(apiPayload);
        console.log("Repsonse ADD", response);
        if (response.status === "success") {
          showSuccess("User added successfully");
          await fetchUsers();
        }
      }

      handleClose();
    } catch (error) {
      console.error("Error in handleSubmit:", error);
    }
  };

  const handleMenuClick = (event, userId) => {
    setAnchorEl(event.currentTarget);
    console.log("Selected User", userId);
    setSelectedUserId(userId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUserId(null);
  };

  const handleEdit = () => {
    const userToEdit = users.find(
      (user) => user.USER_AUTH_ID === selectedUserId
    );
    console.log("User to edit ", selectedUserId);
    if (userToEdit) {
      setEditUser(userToEdit);

      setUserData({
        username: userToEdit.USER_NAME || userToEdit.username || "",
        email: userToEdit.USER_EMAIL || userToEdit.email || "",
        contactNumber: userToEdit.CONTACT_NO || userToEdit.contactNumber || "",
        companyName: userToEdit.companyName || "",
        password: userToEdit.USER_PASSWORD || userToEdit.password || "",
        status: userToEdit.STATUS || userToEdit.status || "",
        role: userToEdit.USER_ROLE || userToEdit.role || "",
      });
      setOpen(true);
    }
    handleMenuClose();
  };



  const handleDelete = async (id) => {
    try {
      const response = await deleteAdmin(id);
      console.log("Response DELETE", response);

      if (response.status === "success") {
        showSuccess("User set to inactive successfully");
        // Refresh the user list immediately
        await fetchUsers();
      } else {
        showError("Failed to deactivate user");
      }
      handleMenuClose();
    } catch (error) {
      showError("Failed to deactivate user");
      console.error("Error deactivating user:", error);
    }
  };

  const handleDeleteClick = (userId) => {
    setUserToDelete(userId);
    setOpenAlert(true);
    handleMenuClose();
  };

  const handleConfirmDelete = () => {
    if (userToDelete) {
      handleDelete(userToDelete);
    }
    setOpenAlert(false);
  };



  const columns = getSettingColumns({
    anchorEl,
    selectedUserId,
    handleMenuClick,
    handleMenuClose,
    handleEdit,
    handleDelete: handleDeleteClick,
    handleColumnFilter,
    columnFilters,
  });

  return (
    <>
      <Box sx={{ p: { xs: 1, sm: 2 }, paddingBottom: 5, width: "100%" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mb: 3,
            mt: { xs: 8, sm: 4, md: 2 },
          }}
        >
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpen}
            disabled={loading}
            size={isMobile ? "small" : "medium"}
            sx={{
              backgroundColor: "#1976d2",
              "&:hover": { backgroundColor: "#1565c0" },
            }}
          >
            Add User
          </Button>
        </Box>

        <Modal open={open} onClose={handleClose}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "95vw", sm: "90vw", md: 500 },
              maxWidth: 500,
              bgcolor: "background.paper",
              boxShadow: 24,
              p: { xs: 2, sm: 3, md: 4 },
              borderRadius: 2,
              maxHeight: "90vh",
              overflow: "auto",
            }}
          >
            <Typography variant={isMobile ? "h6" : "h5"} component="h2" gutterBottom>
              {editUser ? "Edit User" : "Add New User"}
            </Typography>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <UserFormFields
                  userData={userData}
                  handleChange={handleChange}
                  editUser={editUser}
                />
                <Grid
                  item
                  xs={12}
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: { xs: 1, sm: 2 },
                    mt: 2,
                    flexDirection: { xs: "column", sm: "row" }
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={handleClose}
                    disabled={loading}
                    size={isMobile ? "small" : "medium"}
                    fullWidth={isMobile}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    type="submit"
                    disabled={
                      loading ||
                      (!editUser && !isPasswordValid(userData.password)) ||
                      (editUser &&
                        userData.password && userData.password !== editUser?.USER_PASSWORD &&
                        !isPasswordValid(userData.password))
                    }
                    size={isMobile ? "small" : "medium"}
                    fullWidth={isMobile}
                  >
                    {loading ? "Saving..." : editUser ? "Update" : "Save"}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Box>
        </Modal>

        <Paper
          sx={{
            height: { xs: 400, sm: 500, md: 600 },
            width: "100%",
            mt: 2,
            overflowX: "auto",
            "& .MuiDataGrid-root": {
              minWidth: { xs: "600px", sm: "700px" },
            },
          }}
        >
          <DataGrid
            rows={filteredUsers}
            columns={columns}
            pageSize={isMobile ? 3 : 5}
            rowsPerPageOptions={isMobile ? [3] : [5]}
            disableRowSelectionOnClick
            loading={loading}
            getRowId={(row) => row.USER_AUTH_ID}
            components={{
              NoRowsOverlay: () => (
                <Box
                  sx={{
                    height: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="body1" color="textSecondary">
                    {loading
                      ? "Loading users..."
                      : filteredUsers.length === 0 && users.length > 0
                      ? "No users found matching your search."
                      : "No users added yet. Click 'Add User' to get started."}
                  </Typography>
                </Box>
              ),
            }}
            sx={dataGridStyles}
          />
        </Paper>
        <AlertDialogSlide
          open={openAlert}
          lable="Delete User"
          setOpen={setOpenAlert}
          handleDelte={handleConfirmDelete}
          message="Are you sure you want to delete this user? This action cannot be undone."
        />
      </Box>
      <Footer />
    </>
  );
};

export default DashboardSetting;
